import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface UtilityCredentialSecretInput {
  credentialId: string
  organizationId: Organization["id"]
}

/**
 * Login pair from GET .../pipelines/credentials/:id/secret after the
 * httpClient snake_case → camelCase response interceptor.
 * Do not widen list/get-credential meta schemas to include password.
 */
export interface UtilityCredentialSecretResponse {
  id: string
  password: string
  username: string
}

export const useUtilityCredentialSecret = (
  options?: UseMutationOptions<
    UtilityCredentialSecretResponse,
    HttpError,
    UtilityCredentialSecretInput
  >
) => {
  const { setNotification } = useNotification()
  const { t } = useTranslation("common")

  const mutation = useMutation<
    UtilityCredentialSecretResponse,
    HttpError,
    UtilityCredentialSecretInput
  >({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({
      credentialId,
      organizationId,
    }: UtilityCredentialSecretInput) => {
      try {
        const response = await httpClient.get<UtilityCredentialSecretResponse>(
          `/api/v1/organizations/${organizationId}/pipelines/credentials/${credentialId}/secret`
        )

        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (error, ...rest) => {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("admin.credentialsUi.revealPasswordError")
        ),
        type: "error",
      })

      options?.onError?.(error, ...rest)
    },
  })

  return useRenameQueryResult(mutation, ApiQueryName.UtilityCredentialSecret)
}
